import type { LayerOperation } from './assistant-actions'

const MAX_OPERATIONS = 20
const MOVE_TO = ['top', 'bottom', 'above', 'below', 'into', 'out_of'] as const
const MODES = ['independent', 'exclusive'] as const

const text = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v : undefined)
const oneOf = <T extends string>(v: unknown, allowed: readonly T[]): T | undefined =>
  typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : undefined
const invalid = (reason: string): LayerOperation => ({ op: 'invalid', reason })

/** One wire operation (snake_case, model-authored) → a LayerOperation, or `invalid` with why. */
function readOne(raw: unknown): LayerOperation {
  const r = (raw ?? {}) as Record<string, unknown>
  const id = text(r.id)

  switch (r.op) {
    case 'opacity':
      return id && typeof r.percent === 'number'
        ? { op: 'opacity', id, percent: r.percent }
        : invalid('opacity needs `id` and a numeric `percent`')
    case 'move': {
      const to = oneOf(r.to, MOVE_TO)
      if (!id || !to) return invalid('move needs `id` and `to` (top, bottom, above, below, into, out_of)')

      return { op: 'move', id, to, ...(text(r.target_id) ? { targetId: text(r.target_id) } : {}) }
    }

    case 'group':
      return Array.isArray(r.ids) && r.ids.every(x => typeof x === 'string')
        ? { op: 'group', title: typeof r.title === 'string' ? r.title : '', ids: r.ids as string[] }
        : invalid('group needs `ids` (a list of layer or group ids) and `title`')
    case 'ungroup':
      return text(r.group_id) ? { op: 'ungroup', groupId: text(r.group_id)! } : invalid('ungroup needs `group_id`')
    case 'rename_group':
      return text(r.group_id)
        ? { op: 'rename_group', groupId: text(r.group_id)!, title: typeof r.title === 'string' ? r.title : '' }
        : invalid('rename_group needs `group_id` and `title`')
    case 'group_mode': {
      const mode = oneOf(r.mode, MODES)

      return text(r.group_id) && mode
        ? { op: 'group_mode', groupId: text(r.group_id)!, mode }
        : invalid('group_mode needs `group_id` and `mode` (independent or exclusive)')
    }

    case 'show_table':
      return text(r.layer_id)
        ? { op: 'show_table', layerId: text(r.layer_id)! }
        : invalid('show_table needs `layer_id`')
    default:
      return invalid(`unknown op ${JSON.stringify(r.op)}`)
  }
}

/** `update_map_layers` input → operations, or null when `operations` is not a list of 1 to 20. */
export function readLayerOperations(input: unknown): LayerOperation[] | null {
  const ops = (input ?? {}) as { operations?: unknown }
  if (!Array.isArray(ops.operations) || ops.operations.length === 0 || ops.operations.length > MAX_OPERATIONS)
    return null

  return ops.operations.map(readOne)
}
